import type { BatchPagination } from '../types/api';
import type { FilterValues, ListBatch, ListBatchParams, ListSort } from '../hooks/useInfiniteList';

/**
 * Two adapters for useInfiniteList.
 *
 * `queryInMemoryWindow` searches and sorts rows already held in memory, for
 * list surfaces whose endpoint takes no list parameters:
 *   - GET /apps parses only `organizationId` and returns the whole array, which
 *     OrganizationContext caches and other code counts and filters.
 *   - GET /template-processors (the member listing) parses no list parameters.
 *   - GET /org/{orgId}/roles is one organization's roles, a bounded set.
 * The hook reveals the result 50 rows at a time; nothing here slices.
 *
 * DO NOT use it on Sensors, Tags or any admin list. Those endpoints read in
 * batches server-side and grow without a ceiling; holding them in memory would
 * download the whole table.
 *
 * `batchFetcher` adapts an API list method in batch mode (`limit` + `offset` or
 * `cursor`, answered with `{ limit, offset, totalCount, hasMore, nextCursor }`)
 * to the hook's cursor contract.
 */

export type SortableValue = string | number | boolean | null | undefined;

export interface InMemoryListConfig<T, K extends string> {
  /** Fields the search sweeps. Absent values are skipped. */
  searchFields: (row: T) => Array<string | null | undefined>;
  sortAccessors: Record<K, (row: T) => SortableValue>;
  /** Stable secondary key — mirrors the id tiebreak the API applies. */
  tiebreak: (row: T) => string;
}

/**
 * Absent values sort LAST in both directions (the NULLS LAST the API applies),
 * so flipping direction never parades empty rows to the top. Strings compare
 * with localeCompare, booleans as Number(). Pass dates through the accessor as
 * `Date.parse(...)` so they compare numerically.
 */
const compareValues = (a: SortableValue, b: SortableValue): number => {
  const aMissing = a === null || a === undefined || a === '';
  const bMissing = b === null || b === undefined || b === '';
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
  }
  const an = typeof a === 'boolean' ? Number(a) : (a as number);
  const bn = typeof b === 'boolean' ? Number(b) : (b as number);
  if (an < bn) return -1;
  if (an > bn) return 1;
  return 0;
};

const isMissing = (value: SortableValue): boolean =>
  value === null || value === undefined || value === '';

/** The filtered, sorted rows — every one of them. The hook decides how many show. */
export function queryInMemoryWindow<T, K extends string>(
  rows: readonly T[],
  params: { search: string; sort?: ListSort<K> },
  config: InMemoryListConfig<T, K>,
): T[] {
  const { search, sort } = params;

  let working = rows.slice();

  const needle = search.trim().toLowerCase();
  if (needle) {
    working = working.filter((row) =>
      config.searchFields(row).some((field) => {
        if (field === null || field === undefined) return false;
        return field.toLowerCase().includes(needle);
      }),
    );
  }

  if (sort) {
    const accessor = config.sortAccessors[sort.column];
    if (accessor) {
      const factor = sort.direction === 'asc' ? 1 : -1;
      working.sort((rowA, rowB) => {
        const a = accessor(rowA);
        const b = accessor(rowB);

        // NULLS LAST regardless of direction — applied before the factor so a
        // descending sort does not float every empty row to the top.
        const aMissing = isMissing(a);
        const bMissing = isMissing(b);
        if (aMissing !== bMissing) return aMissing ? 1 : -1;

        const primary = compareValues(a, b);
        if (primary !== 0) return primary * factor;

        // The tiebreak follows the sort's direction, as the API's id term does.
        return config.tiebreak(rowA).localeCompare(config.tiebreak(rowB)) * factor;
      });
    }
  }

  return working;
}

/* ------------------------------------------------------------------ */
/* Batch adapter                                                       */
/* ------------------------------------------------------------------ */

export interface BatchRequest<S extends string> {
  limit: number;
  /** Present when continuing by offset (always on the first batch: 0). */
  offset?: number;
  /** Present when continuing by keyset cursor; never together with offset. */
  cursor?: string;
  search: string;
  sort?: ListSort<S>;
  filters: FilterValues;
  signal: AbortSignal;
}

export interface BatchResponse<T> {
  items: T[];
  pagination: BatchPagination;
}

/**
 * The hook's cursor token carries how many rows the server has served, so an
 * offset continuation is exact and survives removals:
 *   "o:<offset>"             continue with offset
 *   "c:<served>:<apiCursor>" continue with the API's keyset cursor
 * The server's `nextCursor` wins when present; `hasMore` with no cursor means
 * continue by offset (a mutable sort). `hasMore` false ends the list — the
 * received count is never compared with the limit.
 */
export function batchFetcher<T, S extends string = string>(
  fn: (request: BatchRequest<S>) => Promise<BatchResponse<T>>,
): {
  fetcher: (params: ListBatchParams<S>) => Promise<ListBatch<T>>;
  cursorAfterRemove: (cursor: string) => string;
} {
  const fetcher = async (params: ListBatchParams<S>): Promise<ListBatch<T>> => {
    const token = parseToken(params.cursor);
    const request: BatchRequest<S> = {
      limit: params.limit,
      search: params.search,
      sort: params.sort,
      filters: params.filters,
      signal: params.signal,
      ...(token.kind === 'cursor' ? { cursor: token.cursor } : { offset: token.served }),
    };
    const response = await fn(request);
    const served = token.served + response.items.length;
    const { hasMore, nextCursor } = response.pagination;
    let next: string | null = null;
    if (nextCursor) next = `c:${served}:${nextCursor}`;
    else if (hasMore) next = `o:${served}`;
    return { items: response.items, nextCursor: next, totalCount: response.pagination.totalCount };
  };

  const cursorAfterRemove = (cursor: string): string => {
    const token = parseToken(cursor);
    const served = Math.max(0, token.served - 1);
    return token.kind === 'cursor' ? `c:${served}:${token.cursor}` : `o:${served}`;
  };

  return { fetcher, cursorAfterRemove };
}

type Token = { kind: 'offset'; served: number } | { kind: 'cursor'; served: number; cursor: string };

function parseToken(raw: string | null): Token {
  if (!raw) return { kind: 'offset', served: 0 };
  if (raw.startsWith('c:')) {
    const rest = raw.slice(2);
    const colon = rest.indexOf(':');
    const served = Number.parseInt(rest.slice(0, colon), 10);
    return { kind: 'cursor', served: Number.isFinite(served) ? served : 0, cursor: rest.slice(colon + 1) };
  }
  const served = Number.parseInt(raw.replace(/^o:/, ''), 10);
  return { kind: 'offset', served: Number.isFinite(served) && served > 0 ? served : 0 };
}

/** Writes a batch request's window onto query params (limit, then cursor or offset). */
export function applyBatchWindow(
  qs: URLSearchParams,
  request: { limit?: number; offset?: number; cursor?: string | null },
): void {
  qs.set('limit', String(request.limit ?? 50));
  if (request.cursor) qs.set('cursor', request.cursor);
  else qs.set('offset', String(request.offset ?? 0));
}
